import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { getAllOrganisations } from "../../common/api/authApi";
import { normalizedLocale } from "../../common/libs/normalizedLocale";

export type SelectOption = { value: string; label: string };
type OrgOption = SelectOption & { iinBin: string; name: string };

const MIN_QUERY_LENGTH = 3;

// Поиск организации для фильтров отчётов (поля «ИИН/БИН» и «Наименование»).
// search-by-iin-bin / search-by-name не отдают id, а фильтрам нужен organizationId,
// поэтому берём список из /organisation/all (только для оператора) и ищем по нему на фронте.
// Выбранная организация показывается в обоих полях.
export function useOrganisationOptions(enabled: boolean) {
  const { t, i18n } = useTranslation();
  const locale = normalizedLocale(i18n.language);

  const [allOrgs, setAllOrgs] = useState<OrgOption[]>([]);
  const [iinQuery, setIinQuery] = useState("");
  const [nameQuery, setNameQuery] = useState("");
  const [selectedOrg, setSelectedOrg] = useState<SelectOption | null>(null);

  useEffect(() => {
    if (!enabled) return;
    getAllOrganisations()
      .then((response: any) => {
        const list = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.content)
              ? response.content
              : [];
        const options = list
          .map((org: any) => {
            const id = org?.id ?? org?.organizationId;
            if (id == null || id === "") return null;
            const iinBin = String(org?.iinBin || org?.bin || org?.iin || "").trim();
            const name = String(
              locale === "kk"
                ? org?.nameKk || org?.nameRu || org?.name || ""
                : org?.nameRu || org?.nameKk || org?.name || "",
            )
              .replace(/\s+/g, " ")
              .trim();
            const label = [iinBin, name].filter(Boolean).join(" ") || String(id);
            return { value: String(id), label, iinBin, name };
          })
          .filter(Boolean) as OrgOption[];
        if (list.length > 0 && options.length === 0) {
          console.warn("/organisation/all: в ответе нет id организаций", list[0]);
        }
        setAllOrgs(options);
      })
      .catch(() => setAllOrgs([]));
  }, [enabled, locale]);

  const filterOrgs = (query: string, mode: "iin" | "name"): SelectOption[] => {
    const q = query.trim().toLowerCase();
    if (q.length < MIN_QUERY_LENGTH) return [];
    return allOrgs.filter((org) =>
      mode === "iin" ? org.iinBin.includes(q) : org.name.toLowerCase().includes(q),
    );
  };

  const withSelected = (options: SelectOption[]) =>
    selectedOrg && !options.some((o) => o.value === selectedOrg.value)
      ? [selectedOrg, ...options]
      : options;

  const iinOptions = useMemo(
    () => withSelected(filterOrgs(iinQuery, "iin")),
    [allOrgs, iinQuery, selectedOrg],
  );
  const nameOptions = useMemo(
    () => withSelected(filterOrgs(nameQuery, "name")),
    [allOrgs, nameQuery, selectedOrg],
  );

  const selectOrg = (value: string, options: SelectOption[]) =>
    setSelectedOrg(options.find((o) => o.value === value) ?? null);

  const reset = () => {
    setSelectedOrg(null);
    setIinQuery("");
    setNameQuery("");
  };

  const noOptionsMessage = ({ inputValue }: { inputValue: string }) =>
    (inputValue || "").trim().length < MIN_QUERY_LENGTH
      ? t("marking_codes_report.organization_hint")
      : t("marking_codes_report.organization_empty");

  return {
    iinOptions,
    nameOptions,
    setIinQuery,
    setNameQuery,
    selectOrg,
    reset,
    noOptionsMessage,
  };
}
